import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { COLORS, FONTS, STATUS_COLORS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

interface ProgressBarProps {
  value: number;
  maxValue?: number;
  showLabel?: boolean;
  height?: number;
  color?: 'success' | 'warning' | 'danger' | 'primary' | 'secondary';
  backgroundColor?: string;
}

export default function ProgressBar({
  value,
  maxValue = 100,
  showLabel = true,
  height = 8,
  color,
  backgroundColor,
}: ProgressBarProps) {
  const { colors, isDark } = useTheme();

  // Use theme-aware background color if not provided
  const trackBgColor = backgroundColor || (isDark ? colors.backgroundAlt : COLORS.grayLight);
  const percentage = Math.min(Math.max((value / maxValue) * 100, 0), 100);

  // Auto-determine color based on percentage if not provided
  const getColor = () => {
    if (color) {
      if (color === 'primary') return COLORS.primary;
      if (color === 'secondary') return COLORS.secondary;
      return STATUS_COLORS[color];
    }

    if (percentage >= 80) return COLORS.success;
    if (percentage >= 50) return COLORS.warning;
    return COLORS.danger;
  };

  return (
    <View style={styles.container}>
      <View style={[styles.track, { height, backgroundColor: trackBgColor }]}>
        <View
          style={[
            styles.fill,
            {
              width: `${percentage}%`,
              backgroundColor: getColor(),
              height,
            },
          ]}
        />
      </View>
      {showLabel && (
        <Text style={[styles.label, { color: getColor() }]}>
          %{percentage.toFixed(2)}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  track: {
    flex: 1,
    borderRadius: 4,
    overflow: 'hidden',
  },
  fill: {
    borderRadius: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: FONTS.numbers,
    marginRight: 8,
    minWidth: 45,
    textAlign: 'left',
  },
});
